/**
 * UI preferences store — typographie, espacement, animations, marks, code.
 *
 * @remarks
 * Migration 1:1 du legacy `sass front/src/store/useUIPreferencesStore.ts`.
 * 22 réglages persistés. Le `applyPreferencesToDocument` pousse tout au DOM
 * via CSS variables et `data-*` attributes.
 *
 * `applyPreferencesToDocument` est appelé :
 * 1. Au chargement initial (`initializeUIPreferences()` exporté)
 * 2. À chaque setter du store (cohérent avec legacy)
 * 3. Sur rehydration depuis localStorage
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { getCodeTheme } from './code-themes';
import {
  ANIMATION_SPEED_OPTIONS,
  DEFAULT_UI_PREFERENCES,
  FONT_SIZE_LIMITS,
  LETTER_SPACING_LIMITS,
  LINE_HEIGHT_LIMITS,
  type AnimationSpeed,
  type ContentLayout,
  type FontFamily,
  type MarkStyle,
  type UIPreferences,
} from './ui-prefs-options';

interface UIPrefsActions {
  setFontFamily: (f: FontFamily) => void;
  setFontSize: (s: number) => void;
  setCodeTheme: (t: string) => void;
  setLineHeight: (h: number) => void;
  setLetterSpacing: (s: number) => void;
  setCompactMode: (c: boolean) => void;
  setContentLayout: (l: ContentLayout) => void;
  setAnimationSpeed: (s: AnimationSpeed) => void;
  setMarkHighlightColor: (c: string) => void;
  setMarkUnderlineColor: (c: string) => void;
  setSeparatorColor: (c: string) => void;
  setEmptyHintColor: (c: string) => void;
  setAnnotationBracketColor: (c: string) => void;
  setMarkHighlightStyle: (s: MarkStyle) => void;
  setMarkUnderlineStyle: (s: MarkStyle) => void;
  setSeparatorStyle: (s: MarkStyle) => void;
  setAnnotationBracketStyle: (s: MarkStyle) => void;
  setDiagramModalSize: (n: number) => void;
  setCodeBlocksCollapsedByDefault: (c: boolean) => void;
  setCodeBlocksShowLineNumbers: (s: boolean) => void;
  resetToDefaults: () => void;
}

type UIPrefsState = UIPreferences & UIPrefsActions;

// ── Helpers ────────────────────────────────────────────────────────────

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

/**
 * Pousse les préférences au DOM (CSS variables + data attributes).
 * Repris 1:1 du legacy.
 */
function applyPreferencesToDocument(prefs: UIPreferences): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Data attributes
  root.setAttribute('data-font-family', prefs.fontFamily);
  root.setAttribute('data-compact-mode', prefs.compactMode ? 'true' : 'false');
  root.setAttribute('data-content-layout', prefs.contentLayout);
  root.setAttribute('data-highlight-style', prefs.markHighlightStyle);
  root.setAttribute('data-underline-style', prefs.markUnderlineStyle);
  root.setAttribute('data-separator-style', prefs.separatorStyle);
  root.setAttribute('data-annotation-bracket-style', prefs.annotationBracketStyle);
  // Lu par la règle CSS qui masque les numéros de ligne des blocs de code qui
  // suivent la préférence (`[data-line-numbers='preference']`, cf. globals.css).
  root.setAttribute('data-code-line-numbers', prefs.codeBlocksShowLineNumbers ? 'true' : 'false');

  // Continuous CSS vars
  root.style.setProperty('--font-size-base', `${String(prefs.fontSize)}px`);
  root.style.setProperty('--line-height-normal', String(prefs.lineHeight));
  root.style.setProperty('--letter-spacing-normal', `${String(prefs.letterSpacing)}em`);

  // Animation speed multiplier
  const animOption = ANIMATION_SPEED_OPTIONS.find((o) => o.id === prefs.animationSpeed);
  const multiplier = animOption?.multiplier ?? 1;
  root.style.setProperty('--animation-speed-multiplier', String(multiplier));

  if (multiplier === 0) {
    root.style.setProperty('--transition-fast', '0s');
    root.style.setProperty('--transition-normal', '0s');
    root.style.setProperty('--transition-slow', '0s');
    root.style.setProperty('--transition-duration-default', '0ms');
  } else {
    root.style.setProperty('--transition-fast', `${String(0.15 / multiplier)}s`);
    root.style.setProperty('--transition-normal', `${String(0.2 / multiplier)}s`);
    root.style.setProperty('--transition-slow', `${String(0.3 / multiplier)}s`);
    root.style.setProperty('--transition-duration-default', `${String(200 / multiplier)}ms`);
  }

  root.style.setProperty('--spacing-multiplier', prefs.compactMode ? '0.75' : '1');

  // Mark colors — set or remove
  const setOrRemove = (name: string, val: string): void => {
    if (val) root.style.setProperty(name, val);
    else root.style.removeProperty(name);
  };
  setOrRemove('--color-mark-highlight', prefs.markHighlightColor);
  setOrRemove('--color-mark-underline', prefs.markUnderlineColor);
  setOrRemove('--color-separator', prefs.separatorColor);
  setOrRemove('--color-empty-hint', prefs.emptyHintColor);
  setOrRemove('--color-mark-annotation-bracket', prefs.annotationBracketColor);

  // Code syntax theme — push 10 CSS vars consommées par <code>/<pre>.
  // `getCodeTheme` fallback sur Monokai si l'id est invalide, donc on set
  // toujours (jamais remove) — un fallback CSS dans `tokens.css` garantit
  // que les blocs de code restent lisibles même si JS n'a pas tourné.
  const codeTheme = getCodeTheme(prefs.codeTheme);
  root.style.setProperty('--code-bg', codeTheme.colors.background);
  root.style.setProperty('--code-fg', codeTheme.colors.foreground);
  root.style.setProperty('--code-comment', codeTheme.colors.comment);
  root.style.setProperty('--code-string', codeTheme.colors.string);
  root.style.setProperty('--code-number', codeTheme.colors.number);
  root.style.setProperty('--code-keyword', codeTheme.colors.keyword);
  root.style.setProperty('--code-function', codeTheme.colors.function);
  root.style.setProperty('--code-operator', codeTheme.colors.operator);
  root.style.setProperty('--code-punctuation', codeTheme.colors.punctuation);
  root.style.setProperty('--code-variable', codeTheme.colors.variable);
}

// ── Store ──────────────────────────────────────────────────────────────

export const useUIPrefsStore = create<UIPrefsState>()(
  devtools(
    persist(
      (set, get) => {
        const setAndApply = (patch: Partial<UIPreferences>): void => {
          const next = { ...get(), ...patch };
          applyPreferencesToDocument(next);
          set(patch as Partial<UIPrefsState>);
        };

        return {
          ...DEFAULT_UI_PREFERENCES,

          setFontFamily: (f) => {
            setAndApply({ fontFamily: f });
          },
          setFontSize: (s) => {
            setAndApply({ fontSize: clamp(s, FONT_SIZE_LIMITS.MIN, FONT_SIZE_LIMITS.MAX) });
          },
          setCodeTheme: (t) => {
            setAndApply({ codeTheme: t });
          },
          setLineHeight: (h) => {
            setAndApply({ lineHeight: clamp(h, LINE_HEIGHT_LIMITS.MIN, LINE_HEIGHT_LIMITS.MAX) });
          },
          setLetterSpacing: (s) => {
            setAndApply({
              letterSpacing: clamp(s, LETTER_SPACING_LIMITS.MIN, LETTER_SPACING_LIMITS.MAX),
            });
          },
          setCompactMode: (c) => {
            setAndApply({ compactMode: c });
          },
          setContentLayout: (l) => {
            setAndApply({ contentLayout: l });
          },
          setAnimationSpeed: (s) => {
            setAndApply({ animationSpeed: s });
          },
          setMarkHighlightColor: (c) => {
            setAndApply({ markHighlightColor: c });
          },
          setMarkUnderlineColor: (c) => {
            setAndApply({ markUnderlineColor: c });
          },
          setSeparatorColor: (c) => {
            setAndApply({ separatorColor: c });
          },
          setEmptyHintColor: (c) => {
            setAndApply({ emptyHintColor: c });
          },
          setAnnotationBracketColor: (c) => {
            setAndApply({ annotationBracketColor: c });
          },
          setMarkHighlightStyle: (s) => {
            setAndApply({ markHighlightStyle: s });
          },
          setMarkUnderlineStyle: (s) => {
            setAndApply({ markUnderlineStyle: s });
          },
          setSeparatorStyle: (s) => {
            setAndApply({ separatorStyle: s });
          },
          setAnnotationBracketStyle: (s) => {
            setAndApply({ annotationBracketStyle: s });
          },
          setDiagramModalSize: (n) => {
            setAndApply({ diagramModalSize: clamp(n, 0.5, 1.0) });
          },
          setCodeBlocksCollapsedByDefault: (c) => {
            set({ codeBlocksCollapsedByDefault: c });
          },
          setCodeBlocksShowLineNumbers: (s) => {
            setAndApply({ codeBlocksShowLineNumbers: s });
          },

          resetToDefaults: () => {
            applyPreferencesToDocument(DEFAULT_UI_PREFERENCES);
            set(DEFAULT_UI_PREFERENCES);
          },
        };
      },
      {
        name: 'saas-front-v2:ui-prefs',
        version: 2,
        partialize: (state) => {
          // Strip toutes les actions — persiste UNIQUEMENT les UIPreferences fields
          const persistable: UIPreferences = {
            fontFamily: state.fontFamily,
            fontSize: state.fontSize,
            codeTheme: state.codeTheme,
            lineHeight: state.lineHeight,
            letterSpacing: state.letterSpacing,
            compactMode: state.compactMode,
            contentLayout: state.contentLayout,
            animationSpeed: state.animationSpeed,
            markHighlightColor: state.markHighlightColor,
            markUnderlineColor: state.markUnderlineColor,
            separatorColor: state.separatorColor,
            emptyHintColor: state.emptyHintColor,
            annotationBracketColor: state.annotationBracketColor,
            markHighlightStyle: state.markHighlightStyle,
            markUnderlineStyle: state.markUnderlineStyle,
            separatorStyle: state.separatorStyle,
            annotationBracketStyle: state.annotationBracketStyle,
            diagramModalSize: state.diagramModalSize,
            codeBlocksCollapsedByDefault: state.codeBlocksCollapsedByDefault,
            codeBlocksShowLineNumbers: state.codeBlocksShowLineNumbers,
          };
          return persistable;
        },
        onRehydrateStorage: () => (state) => {
          if (state) {
            applyPreferencesToDocument(state);
          }
        },
        /**
         * Migration policy : 22 réglages sont persistés. À chaque changement
         * de shape (ajout d'un nouveau pref, renommage), bumper `version` et
         * merger les anciennes prefs avec `DEFAULT_UI_PREFERENCES`. Si le
         * payload est inattendu ou corrompu, retourne les defaults pleins —
         * l'utilisateur perd ses prefs custom mais l'app reste utilisable.
         */
        // ⚠️ Pas de `if (oldVersion === <version courante>)` ici : zustand n'appelle
        // `migrate` que si les versions diffèrent, la branche serait morte. Au
        // prochain bump, adapter AVANT le merge en comparant avec `<` :
        // `if (oldVersion < 3) { …adapter… }`. Cascade de référence :
        // `use-layout-store`. (SAAS-903)
        migrate: (persistedState: unknown) => {
          try {
            // Merge : payload persisté + defaults pour les fields ajoutés depuis.
            // Les 22 réglages sont des scalaires indépendants, donc un merge
            // partiel est sûr quelle que soit la version d'origine — et il préserve
            // les préférences de l'utilisateur au lieu de les écraser.
            if (persistedState !== null && typeof persistedState === 'object') {
              return { ...DEFAULT_UI_PREFERENCES, ...(persistedState as Partial<UIPreferences>) };
            }
            return DEFAULT_UI_PREFERENCES;
          } catch {
            return DEFAULT_UI_PREFERENCES;
          }
        },
      },
    ),
    { name: 'ui-prefs', enabled: import.meta.env.DEV },
  ),
);

/**
 * Initialise les préférences UI **avant** le mount de React (anti-FOUC).
 * À appeler dans `main.tsx` après la lecture localStorage.
 */
export function initializeUIPreferences(): void {
  if (typeof window === 'undefined') return;
  try {
    const stored = localStorage.getItem('saas-front-v2:ui-prefs');
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      const prefs =
        typeof parsed === 'object' && parsed !== null && 'state' in parsed
          ? (parsed as { state: UIPreferences }).state
          : (parsed as UIPreferences);
      applyPreferencesToDocument(prefs);
    } else {
      applyPreferencesToDocument(DEFAULT_UI_PREFERENCES);
    }
  } catch (err) {
    console.error('[useUIPrefsStore] init failed:', err);
    applyPreferencesToDocument(DEFAULT_UI_PREFERENCES);
  }
}
